import React from 'react';
import { Translate } from 'react-localize-redux';
import { PaymentRecord, PaymentMethod } from './types';
import { formatCurrency, fullDateFromPostgresString, researcherFullName } from '../../../utils';
import Modal from '../../../components/general/modal';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import ChipPaymentMethod from './ChipPaymentMethod';

interface PaymentRecordsModalProps {
    open: boolean;
    paymentRecords: PaymentRecord[];
    paymentMethods: PaymentMethod[];
    researcherMap: Record<string, any>;
    currency: string;
    languageCode: string;
    onClose: () => void;
}

/**
 * Reusable modal component to display payment records in a table
 */
const PaymentRecordsModal: React.FC<PaymentRecordsModalProps> = ({
    open,
    paymentRecords,
    paymentMethods,
    researcherMap,
    currency,
    languageCode,
    onClose
}) => {
    // Prepare headCells for EnhancedTable
    const headCells = [
        { id: 'id', label: <Translate id="hospital.billing.bill.payment_id" />, alignment: 'left' },
        { id: 'amount', label: <Translate id="hospital.billing.bill.amount" />, alignment: 'right' },
        { id: 'paymentMethod', label: <Translate id="hospital.billing.bill.payment_method" />, alignment: 'center' },
        { id: 'details', label: <Translate id="hospital.billing.bill.payment_details" />, alignment: 'left' },
        { id: 'paidTo', label: <Translate id="hospital.billing.bill.cashier" />, alignment: 'left' },
        { id: 'paidAt', label: <Translate id="hospital.billing.bill.payment_date" />, alignment: 'left' },
    ];

    // Prepare rows for EnhancedTable
    const rows = paymentRecords.map((rec) => {
        const paymentMethodObj = (paymentMethods || []).find((pm: PaymentMethod) => pm.id === rec.paymentMethodInvestigationId);
        const paymentMethodCode = rec.paymentData?.type === "insurance_payment" ? 'INSURANCE' : paymentMethodObj?.code || '';
        const paymentMethodLabel = rec.paymentData?.type === "insurance_payment" ? 'Insurance' : (paymentMethodObj?.customName || paymentMethodObj?.name || rec.paymentMethodInvestigationId);
        const paidTo = typeof rec.createdByResearcherUuid === 'string' && researcherMap[rec.createdByResearcherUuid]
                ? researcherFullName(researcherMap[rec.createdByResearcherUuid])
                : (rec.createdByResearcherUuid || '');
        
        // Build payment details string
        let paymentDetails = '';
        if (paymentMethodCode === 'CHEQUE' && rec.paymentData?.chequeNumber) {
            paymentDetails = `Bank: ${rec.paymentData.bankName || 'N/A'} - Cheque: ${rec.paymentData.chequeNumber}`;
        } else if (rec.paymentData?.note || rec.paymentData?.reference) {
            paymentDetails = rec.paymentData.note || rec.paymentData.reference || '';
        }

        return {
            id: rec.id,
            amount: formatCurrency(rec.amount, currency, languageCode),
            paymentMethod: (
                <ChipPaymentMethod code={paymentMethodCode} label={paymentMethodLabel} size="small" />
            ),
            details: paymentDetails,
            paidTo: paidTo,
            paidAt: fullDateFromPostgresString(languageCode, rec.paidAt),
            createdAt: fullDateFromPostgresString(languageCode, rec.createdAt),
            note: rec.paymentData?.note || rec.paymentData?.reference || '',
        }
    });

    if (!open) return null;

    return (
        <Modal
            key="modal-payment-records"
            open={true}
            title={<Translate id="hospital.billing.bill.payment_records" />}
            closeModal={onClose}
        >
            <div style={{ minWidth: 600 }}>
                <EnhancedTable
                    rows={rows}
                    headCells={headCells}
                    noSelectable
                    noHeader
                    noFooter={rows.length <= 10}
                />
            </div>
        </Modal>
    );
};

export default PaymentRecordsModal;
